import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { useMode } from '../context/ModeContext';
import { messageAPI, notificationAPI } from '../services/api';
import ModeToggle from './ModeToggle';
import GoLiveToggle from './GoLiveToggle';
import './Navbar.css';

const Navbar = ({ onShowHelp }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { connected, notificationCount: wsNotificationCount, setNotificationCount: setWsNotificationCount } = useWebSocket();
  const { mode, isDualModeEnabled } = useMode();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);

  // Sync WebSocket notification count with local state
  useEffect(() => {
    if (wsNotificationCount !== null && wsNotificationCount !== undefined) {
      setNotificationCount(wsNotificationCount);
    }
  }, [wsNotificationCount]);

  useEffect(() => {
    // Fetch initial counts
    if (isAuthenticated) {
      fetchUnreadCount();
      fetchNotificationCount();
      // Only poll messages (not notifications since WebSocket handles that)
      const interval = setInterval(() => {
        fetchUnreadCount();
        // Fetch notification count if WebSocket is not connected
        if (!connected) {
          fetchNotificationCount();
        }
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, connected]);

  const fetchUnreadCount = async () => {
    try {
      const response = await messageAPI.getUnreadCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      const count = response.data.count || 0;
      setNotificationCount(count);
      // Update WebSocket context too
      if (setWsNotificationCount) {
        setWsNotificationCount(count);
      }
    } catch (err) {
      console.error('Error fetching notification count:', err);
    }
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    // Use window.location for a full page reload to ensure clean state
    window.location.href = '/login';
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="navbar" role="navigation" aria-label="Main navigation">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" aria-label="Real Time Repairs - Go to homepage">
          <img src="/logo-circle.webp" alt="" className="navbar-logo-image" aria-hidden="true" width="36" height="36" />
          <span className="navbar-logo-text">Real Time <span className="navbar-logo-accent">Repairs</span></span>
        </Link>

        {/* M6 fix: Added aria-expanded and aria-controls for accessibility */}
        <button
          className="navbar-hamburger"
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="navbar-menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Mode Toggle - Only for contractors */}
        {isAuthenticated && isDualModeEnabled && (
          <ModeToggle />
        )}

        {/* M6 fix: Added id and role for accessibility */}
        <div id="navbar-menu" className={`navbar-menu ${mobileMenuOpen ? 'active' : ''}`} role="menubar">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="navbar-link" onClick={closeMobileMenu}>
                Dashboard
              </Link>

              {/* Homeowner view: shown when toggled to homeowner OR (for non-admin users) when their account is homeowner/business. Admins are mode-driven so the toggle actually flips their view. */}
              {(mode === 'homeowner' || (!user?.is_admin && (user?.user_type === 'homeowner' || user?.user_type === 'business'))) && (
                <>
                  <Link to="/jobs" className="navbar-link" onClick={closeMobileMenu}>
                    My Jobs
                  </Link>
                  <Link to="/need-help-now" className="navbar-link navbar-link-highlight" onClick={closeMobileMenu}>
                    ⚡ Need Help Now
                  </Link>
                  <Link to="/jobs/new" className="navbar-link" onClick={closeMobileMenu}>
                    Post a Job
                  </Link>
                  <Link to="/contractors" className="navbar-link" onClick={closeMobileMenu}>
                    Find Contractors
                  </Link>
                </>
              )}

              {/* Contractor (business) view: shown when toggled to business AND (real contractor OR admin) */}
              {mode === 'business' && (user?.user_type === 'contractor' || user?.is_admin) && (
                <>
                  <Link to="/jobs" className="navbar-link" onClick={closeMobileMenu}>
                    Browse Jobs
                  </Link>
                  <Link to="/my-bids" className="navbar-link" onClick={closeMobileMenu}>
                    My Bids
                  </Link>
                  <Link to="/job-alerts" className="navbar-link" onClick={closeMobileMenu}>
                    Job Alerts
                  </Link>
                  <Link to="/contractors" className="navbar-link" onClick={closeMobileMenu}>
                    Find Subcontractors
                  </Link>
                  <Link to="/tools" className="navbar-link" onClick={closeMobileMenu}>
                    Tools
                  </Link>
                </>
              )}

              {/* HOA Community */}
              {((user?.user_type === 'homeowner' && user?.hoa_member === true) || (user?.user_type === 'business' && user?.organization?.is_hoa === true)) && (
                <Link to="/hoa-community" className="navbar-link" onClick={closeMobileMenu}>
                  HOA Community
                </Link>
              )}

              {/* Maintenance - For homeowner view (mode-driven for admins) */}
              {(mode === 'homeowner' || (!user?.is_admin && (user?.user_type === 'homeowner' || user?.user_type === 'business'))) && (
                <Link to="/maintenance" className="navbar-link" onClick={closeMobileMenu}>
                  Maintenance
                </Link>
              )}

              {/* DIY - Shown in homeowner view (mode-driven for contractors and admins; always for real homeowners not in admin mode) */}
              {((mode === 'homeowner' && (user?.user_type === 'contractor' || user?.is_admin)) || (user?.user_type === 'homeowner' && !user?.is_admin)) && (
                <Link to="/diy" className="navbar-link" onClick={closeMobileMenu}>
                  DIY
                </Link>
              )}
              <Link to="/invoices" className="navbar-link" onClick={closeMobileMenu}>
                Invoices
              </Link>
              <Link to="/messages" className="navbar-link messages-link" onClick={closeMobileMenu}>
                Messages
                {unreadCount > 0 && (
                  <span className="unread-badge" aria-label={`${unreadCount} unread messages`}>{unreadCount}</span>
                )}
              </Link>
              <Link to="/profile" className="navbar-link" onClick={closeMobileMenu}>
                Profile
              </Link>
              <Link to="/help" className="navbar-link help-btn" onClick={closeMobileMenu}>
                <span className="help-icon">?</span> Help
              </Link>
              {(user?.is_admin || user?.user_type === 'admin') && (
                <Link to="/admin" className="navbar-link admin-link" onClick={closeMobileMenu}>
                  Admin
                </Link>
              )}
              {user?.user_type === 'contractor' && <GoLiveToggle />}
              <button onClick={handleLogout} className="navbar-btn navbar-btn-logout">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="navbar-btn navbar-btn-primary" onClick={closeMobileMenu}>
                Login
              </Link>
              <Link to="/register" className="navbar-btn navbar-btn-secondary" onClick={closeMobileMenu}>
                Sign Up
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
